/**
 * <s-recently-viewed>
 * mode="record": remembers the current product URL (attribute `url`) in this browser.
 * mode="display": fills its `[data-recent-grid]` from `template[data-recent-card]`
 * (`.js` product JSON) for the visitor's recently viewed products, and swaps the
 * `[data-recent-fallback]` / `[data-recent-only]` regions. Without history the
 * server-rendered fallback (new arrivals) stays untouched.
 */
const STORAGE_KEY = 'alqalam:recently-viewed';
const MAX_ITEMS = 8;

function readHistory() {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeHistory(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
  } catch {
    /* Storage can be unavailable (private mode); the fallback still renders. */
  }
}

function cleanTitle(title) {
  return [" – ", " — ", " | ", ": ", " - "].reduce((value, separator) => value.split(separator)[0], title).trim();
}

class RecentlyViewedElement extends HTMLElement {
  connectedCallback() {
    const mode = this.getAttribute('mode');
    if (mode === 'record') {
      this.#record();
    } else if (mode === 'display') {
      this.#display();
    }
  }

  #record() {
    const url = (this.getAttribute('url') || '').split('?')[0];
    if (!url) return;
    const history = readHistory().filter((item) => item !== url);
    history.unshift(url);
    writeHistory(history);
  }

  async #display() {
    const history = readHistory();
    const grid = this.querySelector('[data-recent-grid]');
    const template = this.querySelector('template[data-recent-card]');
    if (!history.length || !grid || !template) return;

    const currency = this.getAttribute('currency') || 'INR';
    const locale = document.documentElement.lang || 'en-IN';
    const format = (cents) => {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch {
        return `${currency} ${(cents / 100).toFixed(2)}`;
      }
    };

    const products = await Promise.all(
      history.map(async (url) => {
        try {
          const response = await fetch(`${url}.js`, { credentials: 'same-origin' });
          return response.ok ? await response.json() : null;
        } catch {
          return null;
        }
      }),
    );

    const items = products.filter((product) => product && product.url && product.title);
    if (!items.length) return;

    const fragment = document.createDocumentFragment();
    for (const product of items) {
      const card = template.content.cloneNode(true);
      const title = cleanTitle(product.title);
      card.querySelectorAll('a[data-recent-link]').forEach((link) => {
        link.href = product.url.split('?')[0];
        link.setAttribute('aria-label', product.title);
      });
      const image = card.querySelector('img[data-recent-image]');
      if (image) {
        if (product.featured_image) {
          const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
          const join = src.includes('?') ? '&' : '?';
          image.src = `${src}${join}width=600`;
          image.srcset = [300, 450, 600].map((w) => `${src}${join}width=${w} ${w}w`).join(', ');
          image.alt = product.title;
        } else {
          image.remove();
        }
      }
      const titleNode = card.querySelector('[data-recent-title]');
      if (titleNode) titleNode.textContent = title;
      // Cart-icon quick add opens the page's shared quick-add modal; sold-out products get no button.
      const quickAdd = card.querySelector('[data-recent-quick-add]');
      if (quickAdd) {
        if (product.available === false) {
          quickAdd.remove();
        } else {
          quickAdd.dataset.productUrl = product.url.split('?')[0];
          const label = quickAdd.querySelector('[data-recent-quick-add-label]');
          if (label) label.textContent = `${label.textContent}: ${title}`;
        }
      }
      const priceNode = card.querySelector('[data-recent-price]');
      if (priceNode) {
        const min = product.price_min ?? product.price;
        priceNode.textContent = format(min);
        const compare = card.querySelector('[data-recent-compare]');
        if (compare) {
          if (product.compare_at_price && product.compare_at_price > product.price && product.price_varies === false) {
            compare.textContent = format(product.compare_at_price);
            compare.hidden = false;
          }
        }
      }
      fragment.appendChild(card);
    }

    grid.replaceChildren(fragment);
    this.querySelectorAll('[data-recent-fallback]').forEach((node) => {
      node.hidden = true;
    });
    this.querySelectorAll('[data-recent-only]').forEach((node) => {
      node.hidden = false;
    });
  }
}

if (!customElements.get('s-recently-viewed')) {
  customElements.define('s-recently-viewed', RecentlyViewedElement);
}
